/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useRef, useState } from "react";
import Head from "next/head";
import Image from "next/image";

import { BookOpen, CircleCheckBig, Gem, QrCode, Star, Users, X } from "lucide-react";

import useCalApi from "@/components/hooks/useCalApi";
import { useCalendarLink } from "@/components/hooks/useCalendarLink";
import {
  CostlyArrow,
  LessEfficientArrow,
  NumbersArrow,
  TimeConsumingArrow,
  UnderlineSVG,
} from "@/components/icons/HomePage";
import { NumberTicker } from "@/components/magicui/number-ticker";
import Footer from "@/components/marketing/pieces/Footer";
import NavbarLegacy from "@/components/marketing/pieces/NavbarLegacy";
import VideoModalWithThumbnail from "@/components/seo/VideoModalWithThumbnail";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PRODUCTS } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { clashGrotesk, jakartaSans } from "@/styles/fonts";

import type { CarouselApi } from "@/components/ui/carousel";

export const COMPANY = [
  {
    name: "About",
    slug: "about",
    icon: Users,
  },
  {
    name: "Blog",
    slug: "resources",
    icon: BookOpen,
  },
] as const;

export const TOOLS = [
  {
    name: "Google Review QR Code Generator",
    slug: "google-reviews-qr-code-creator",
    icon: QrCode,
  },
  {
    name: "Google Business Profile QR Code Generator",
    slug: "gbp-qr-code-generator",
    icon: QrCode,
  },
] as const;

const FAQ_ITEMS = [
  {
    question: "Are you an agency or a software company?",
    answer:
      "We are a software company that provides marketing services to home service businesses but with a twist: we use AI to make everything faster and more affordable.",
  },
  {
    question: "Will I be able to edit the site?",
    answer:
      "Yes, our easy to use website builder allows you to customize your site: including adding pages, changing colors, and more.",
  },
  {
    question: "Do I still own my domain?",
    answer: "Yes, you will own and have complete control over your domain. We don't hold it hostage!",
  },
  {
    question: "What happens to my old site?",
    answer:
      "We will point your domain to your new Rebolt site, anyone who visits your old site will be redirected to your brand new one.",
  },
  {
    question: "Will I ever own the site outright?",
    answer:
      "Because the website is intertwined into our platform, it makes it difficult for us to export the site and give it to you outright. You own all the content and imagery on the website.",
  },
  {
    question: "How long does it take to rank on google?",
    answer:
      "The time it takes to rank on google depends on your industry and competition, we've often seen improvements within the first 1 to 2 months, but sometimes as early as 1 week!",
  },
] as const;

const TESTIMONIALS = [
  {
    author: "Joe P.",
    content: "Just wanted to say the website worked... so far its introduced me to 2 high quality leads",
    logo: "/ParkerLandscape.webp",
  },
  {
    author: "Julie C",
    content: "Just used the Social Media Management tool for the first time... this is bloody brilliant!!",
    logo: "/CoastalColorsLogo.png",
  },

  {
    author: "Chris L.",
    content:
      "OMG! Looks incredibly amazing for my first impression on my phone! Your team knocked it out of the park. THANK YOU SO MUCH!",
    logo: "/HomePro.webp",
  },
  {
    author: "Ricardo R.",
    content: "Rebolt has definitely helped us expand our business and get a better client base",
    logo: "/1354Logo.webp",
    videoUrl: "https://ik.imagekit.io/4wu305uo4/testimonials/ricardo.mov/ik-video.mp4",
    thumbnailUrl: "https://ik.imagekit.io/4wu305uo4/testimonials/thumbnails/ricardo.png?updatedAt=1741127513034",
  },
  {
    author: "Harold H.",
    content: "The Link in Bio tool has been a complete game changer for our company!!",
    logo: "/bcbalogo.jpg",
  },
  {
    author: "Aidan S.",
    content: "I've gotten so many compliments from all my clients on how nice my site looks! ",
    logo: "/BasinLogo.png",
    videoUrl: "https://ik.imagekit.io/4wu305uo4/testimonials/basin.mov/ik-video.mp4",
    thumbnailUrl: "https://ik.imagekit.io/4wu305uo4/testimonials/thumbnails/basin.png",
  },
  {
    author: "Christine L.",
    content: "I got my first lead request from the website today!! Holy sh*t that was fast!",
    logo: "/PacificBreezeLogo.webp",
  },
  {
    author: "Supreme Klean",
    content: "I'm already ranking higher on the Google page!!!!!! You guys are the BEST!!!!",
    logo: "/SupremeKlean.webp",
  },
  {
    author: "Jeff S.",
    content:
      "I just wanted to say that I've found your website builder to be very simple to work with, and that's exactly what I was looking for!",
    logo: "/131Landscaping.webp",
  },
  {
    author: "Hoyt C.",
    content: "Got a call today purely from SEO which has basically never happened before. Site is working!",
    logo: "/TexianFence.webp",
  },
  {
    author: "Edy M.",
    content: "Our website is performing phenomenally, already got 5 calls in less than a week!",
    logo: "/EdyPressureWash.webp",
  },
  {
    author: "Aziz A.",
    content: "Definitely the smoothest onboarding I've ever been through",
    logo: "/BabylonLogo.png",
  },
];

export default function HomeLanding() {
  useCalApi();

  const { calPath } = useCalendarLink();

  return (
    <>
      <Head>
        <title>Rebolt vs. Townsquare Interactive</title>
        <meta
          name="description"
          content="Quickly build a professional website with Rebolt—no upfront costs! Opt for our DIY tool or let us handle everything. Go live in 24 hours!"
        />

        <meta
          property="og:image"
          content="https://api.screenshotone.com/take?access_key=cQWOH7FyQTatDw&url=https://rebolthq.com&full_page=false&viewport_width=1200&viewport_height=850&device_scale_factor=1&format=jpg&image_quality=80&cache=true&cache_ttl=304800"
        />
      </Head>

      <NavbarLegacy />

      <main className={cn(" overflow-hidden pt-16 bg-ash-100 relative", jakartaSans.variable, clashGrotesk.variable)}>
        <div className="absolute inset-0 w-full h-screen">
          <Image src="/HeroBG.png" alt="Hero" width={1920} height={1080} className="object-cover w-full" priority />
        </div>

        {/* Rest of content - add relative to ensure it stays above the hero image */}
        <div className="flex flex-col items-center justify-center relative">
          {/* Hero */}
          <section className="hero h-[90vh] flex flex-col items-center justify-center gap-8 md:gap-10">
            <div className="flex flex-col gap-6 items-center justify-center px-4 max-w-[992px] text-ash-900">
              <div className="relative text-base lg:text-lg text-ash-900 font-medium tracking-snug -mt-20">
                <span className="px-5 relative inline-block before:content-[''] before:absolute before:-inset-3 before:block before:bg-rebolt-500/10 ">
                  <Gem className="w-6 h-6 text-rebolt-600 mr-4 inline-block" />
                  <span className="relative z-10">Trusted by</span>{" "}
                  <NumberTicker className="text-ash-900 font-semibold relative z-10 tracking-snug" value={500} />
                  <span className="font-semibold relative z-10">+</span>
                  <span className="relative z-10"> service businesses</span>
                </span>
              </div>

              <h1 className="font-clash my-8 font-medium text-5xl md:text-7xl leading-[0.95] tracking-snug text-center relative">
                Don't settle for poor service.
                <span className="relative inline-block">
                  Try Rebolt at no upfront cost
                  <UnderlineSVG className="absolute -bottom-2.5 md:-bottom-4 left-1/2 -translate-x-1/2 w-full" />
                </span>{" "}
              </h1>

              <div className="flex flex-col gap-8">
                <p className="text-base md:text-xl text-center  max-w-[760px]">
                  Start generating more leads and supercharge your business. Get a free preview of your SEO-optimized
                  website today.
                </p>

                <div className="flex w-full justify-center">
                  <Button
                    size="lg"
                    variant="rebolt-secondary"
                    data-cal-link={calPath}
                    data-cal-config='{"layout":"month_view"}'
                  >
                    Get a free demo
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <ProductCarousel />

          {/* Partners */}
          <div className="px-4 lg:px-6 w-full max-w-(--breakpoint-2xl) pb-4 md:pb-0 -mt-40 lg:-mt-48">
            <div className="partners w-full bg-white pt-64 pb-24 rounded-3xl flex flex-col items-center justify-center gap-8">
              <h3 className="text-lg md:text-xl text-rebolt-500 font-semibold uppercase line-height-[1.5] tracking-[0.05em]">
                Partnered with the best
              </h3>
              <div className="flex items-center gap-8 justify-between w-full flex-wrap max-w-5xl px-12 sm:px-4">
                <Image src="/ntca.png" alt="Partner" width={100} height={100} className="h-20 w-auto grayscale " />
                <Image src="/ewing-logo.png" alt="Partner" width={100} height={100} className="h-20 w-auto grayscale" />
                <Image
                  src="/roofle-partner.png"
                  alt="Partner"
                  width={100}
                  height={100}
                  className="h-20 w-auto grayscale "
                />
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="px-4 lg:px-6 w-full py-4 md:pb-0 max-w-(--breakpoint-2xl)">
            <div className="w-full bg-ash-900 min-h-[512px] rounded-3xl flex flex-col items-center justify-center px-4 gap-8 relative">
              <CostlyArrow className="absolute bottom-5 left-5 md:bottom-10 md:left-10 h-28 w-28 md:h-32 md:w-32" />
              <TimeConsumingArrow className="absolute top-5 right-5 md:top-10 md:right-10 h-28 w-28 md:h-32 md:w-32" />
              <LessEfficientArrow className="absolute bottom-5 right-5 md:bottom-10 md:right-10 h-28 w-28 md:h-32 md:w-32" />
              <h3 className="font-clash font-medium text-4xl md:text-6xl leading-[1.1] tracking-snug text-center relative text-white max-w-3xl">
                Traditional marketing agencies take longer, cost more, and deliver less.
              </h3>
            </div>
          </div>

          {/* OFFERS */}
          <OffersSection />

          {/* PRODUCT CARDS */}
          <ProductCardsSection />

          {/* TESTIMONIALS */}
          <TestimonialsSection />

          {/* <div className="w-full max-w-(--breakpoint-2xl) px-4 relative">
            <Image
              src="/ProductDemoGridBG.png"
              alt="Product"
              width={1000}
              height={1000}
              className="object-cover w-full h-full absolute inset-0 -z-10 "
            />
            <div className="mt-6 py-16 md:py-24">
              <div className="relative isolate">
                <Image src="/HeroGrid.png" alt="grid" fill className="absolute inset-0 w-full object-cover -z-10 " />
                <AnimatedBeamDemo />
              </div>
            </div>
          </div> */}

          {/* TABLE */}
          <div className="px-4 w-full max-w-(--breakpoint-2xl) relative isolate">
            <div className="relative isolate">
              <Image src="/HeroGrid.png" alt="grid" fill className="absolute inset-0 w-full object-cover -z-10 " />
              <div className="my-16 md:my-24 relative">
                <div className="flex flex-col items-center justify-center gap-16">
                  <h3 className="font-clash font-medium text-4xl md:text-6xl leading-[1.1] tracking-snug text-center relative">
                    How we{" "}
                    <span className="relative inline-block">
                      compare
                      <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-full">
                        <UnderlineSVG className="w-full" />
                      </span>
                    </span>
                  </h3>
                  <ComparisonTable />
                </div>
              </div>
            </div>
          </div>

          {/* NUMBERS */}
          <NumbersSection />

          {/* FAQ */}
          <FAQSection />

          {/* FOOTER */}
          <Footer />
        </div>
      </main>
    </>
  );
}

export function ProductCarousel() {
  const [selectedProduct, setSelectedProduct] = useState(0);
  const [api, setApi] = useState<CarouselApi>();
  const [buttonsApi] = useState<CarouselApi>();
  const carouselRef = useRef<HTMLDivElement>(null);

  const scrollTo = useCallback(
    (index: number) => {
      api?.scrollTo(index);
      buttonsApi?.scrollTo(index);
      setSelectedProduct(index);

      if (typeof window !== "undefined" && carouselRef.current) {
        const carouselPosition = carouselRef.current.getBoundingClientRect().top;
        const scrollPosition = window.scrollY + carouselPosition;
        const navOffset = 150;

        // Scroll the ProductCarousel to just below the fixed nav
        window.scrollTo({
          top: scrollPosition - navOffset,
          behavior: "smooth",
        });
      }
    },
    [api, buttonsApi]
  );

  useEffect(() => {
    if (api) {
      api.scrollTo(0);
      api.on("select", () => {
        const selected = api.selectedScrollSnap();
        setSelectedProduct(selected);
        buttonsApi?.scrollTo(selected);
      });
    }
  }, [api, buttonsApi]);

  return (
    <>
      <div className="w-full overflow-x-auto px-4 md:px-0 -mt-8">
        <div className="flex min-w-max justify-center divide-x divide-ash-300/30">
          {PRODUCTS.map((product, index) => {
            const Icon = product.icon;
            return (
              <Button
                key={index}
                variant="rebolt-default"
                className={cn(
                  "items-center gap-2 transition-colors whitespace-nowrap relative lg:px-8 pb-7 pt-6 border-0 bg-transparent rounded-none",
                  selectedProduct === index
                    ? "text-rebolt-500 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-rebolt-500"
                    : "text-ash-600 hover:text-ash-900"
                )}
                onClick={() => {
                  scrollTo(index);
                }}
              >
                <Icon className="w-4 h-4 hidden xl:inline-block" />
                <span className="text-sm md:text-base font-semibold">{product.shortName}</span>
              </Button>
            );
          })}
        </div>
      </div>
      <div className="w-full" ref={carouselRef}>
        <Carousel
          opts={{
            align: "start",
            loop: false,
            skipSnaps: false,
            dragFree: true,
            startIndex: 0,
          }}
          setApi={setApi}
          className="w-full"
          onScroll={() => {
            if (api) {
              const selected = api.selectedScrollSnap();
              setSelectedProduct(selected);
              buttonsApi?.scrollTo(selected);
            }
          }}
        >
          <CarouselContent className="gap-20 mx-4 lg:mx-10">
            {PRODUCTS.map((product) => (
              <CarouselItem key={product.slug} className="basis-full pb-2 pl-0">
                <div className="relative h-[500px] lg:h-[650px] w-full bg-white rounded-lg transition-transform shadow-md p-6 md:pl-14 md:pt-16 select-none overflow-hidden">
                  <section className="max-w-7xl mx-auto flex flex-col gap-6 justify-start">
                    <div className="flex items-center gap-3 md:gap-5 mx-auto">
                      {<product.carouselIcon className="w-8 h-8 md:w-12 md:h-12 " />}

                      <h4 className="font-semibold text-xl sm:text-3xl md:text-5xl leading-[1.1] tracking-snug relative text-ash-900 ">
                        {product.longName}
                      </h4>
                    </div>

                    <p className="max-w-3xl text-center text-lg mx-auto font-medium leading-normal text-ash-600">
                      {product.description}
                    </p>

                    <div className="absolute left-1/2 -translate-x-1/2 object-cover w-full max-w-lg md:max-w-xl lg:max-w-3xl  h-full -bottom-32">
                      <product.illustration />
                    </div>
                  </section>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </>
  );
}

export function TestimonialCard({
  testimonial,
}: {
  testimonial: {
    author: string;
    content: string;
    logo?: string;
    image?: string;
    videoUrl?: string;
    thumbnailUrl?: string;
  };
}) {
  return (
    <div className="flex flex-col gap-6 ">
      <div className="flex gap-1">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="h-4 w-4 fill-current text-rebolt-500" />
        ))}
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-4">
          <p className="text-lg md:text-xl font-medium leading-normal">{testimonial.content}</p>
          <div className="flex gap-4 items-center">
            {testimonial.image && (
              <Image src={testimonial.image} alt="image" height={48} width={48} className="rounded-full w-12" />
            )}
            {testimonial.logo && <Image src={testimonial.logo} alt="logo" width={100} height={100} className="w-10" />}
            <p className="text-lg font-semibold text-ash-300">{testimonial.author}</p>
          </div>
          {testimonial.videoUrl && testimonial.thumbnailUrl && (
            <VideoModalWithThumbnail
              thumbnailUrl={testimonial.thumbnailUrl}
              videoUrl={testimonial.videoUrl}
              className="h-48 sm:h-64"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export function TestimonialsSection() {
  return (
    <div className="relative overflow-y-clip px-4">
      {/* Gradient overlays */}
      <div className="absolute inset-x-0 top-0 h-24 bg-linear-to-t from-transparent to-ash-100 pointer-events-none z-10"></div>
      <div className="absolute left-0 top-0 h-24 w-full -translate-x-[calc(100%+8px)] bg-linear-to-t from-transparent to-ash-100 pointer-events-none z-10"></div>
      <div className="absolute right-0 top-0 h-24 w-[400px] translate-x-[calc(100%+8px)] bg-linear-to-t from-transparent to-ash-100 pointer-events-none z-10"></div>
      <div className="absolute left-0 bottom-0 h-24 w-full -translate-x-[calc(100%+8px)] bg-linear-to-b from-transparent to-ash-100 pointer-events-none z-10"></div>
      <div className="absolute right-0 bottom-0 h-24 w-[400px] translate-x-[calc(100%+8px)] bg-linear-to-b from-transparent to-ash-100 pointer-events-none z-10"></div>

      <h3 className="font-clash font-medium text-4xl md:text-6xl leading-[1.1] tracking-snug text-center relative mt-16 md:mt-24 mb-8 md:mb-16 z-10 px-4">
        Real customers.
        <br />
        <span className="relative inline-block">
          Real improvements.
          <span className="absolute -bottom-2 sm:-bottom-4 left-1/2 -translate-x-1/2 w-full">
            <UnderlineSVG className="w-full" />
          </span>
        </span>
      </h3>

      {/* Mobile Carousel */}
      <div className="md:hidden px-0 mx-auto w-screen overflow-visible">
        <Carousel
          opts={{
            align: "start",
            loop: true,
            containScroll: false,
          }}
          className="w-full"
        >
          <CarouselContent>
            {TESTIMONIALS.map((testimonial, index) => (
              <CarouselItem key={index} className="pl-8 basis-[85%] md:basis-[85%] shrink-0">
                <div className="w-full rounded-2xl bg-white p-6">
                  <TestimonialCard testimonial={testimonial} />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex justify-center gap-2 mt-4">
            <CarouselPrevious className="static translate-y-0" />
            <CarouselNext className="static translate-y-0" />
          </div>
        </Carousel>
      </div>

      {/* Desktop Grid */}
      <div className="hidden md:block gap-6 md:columns-2 lg:columns-3 px-4 max-w-7xl mx-auto relative">
        {/* Top blank cards */}
        <div className="absolute left-0 right-0 -top-[calc(322px+24px)] flex gap-6 justify-center">
          <div className="hidden md:block rounded-2xl bg-white opacity-30 h-[322px] w-[400px]"></div>
          <div className="hidden md:block rounded-2xl bg-white opacity-30 h-[322px] w-[400px]"></div>
          <div className="hidden lg:block rounded-2xl bg-white opacity-30 h-[322px] w-[400px]"></div>
        </div>

        {TESTIMONIALS.map((testimonial, index) => (
          <div key={index} className="break-inside-avoid rounded-2xl bg-white p-6 mb-6">
            <TestimonialCard testimonial={testimonial} />
          </div>
        ))}

        {/* Bottom blank cards */}
        <div className="absolute left-0 right-0 -bottom-[calc(322px+24px)] flex gap-6 justify-center">
          <div className="hidden md:block rounded-2xl bg-white opacity-30 h-[322px] w-[400px]"></div>
          <div className="hidden md:block rounded-2xl bg-white opacity-30 h-[322px] w-[400px]"></div>
          <div className="hidden lg:block rounded-2xl bg-white opacity-30 h-[322px] w-[400px]"></div>
        </div>
      </div>

      {/* Left blank cards */}
      <div className="absolute left-0 top-0 hidden md:flex flex-col gap-6 -translate-x-[calc(100%+8px)]">
        {[...Array(Math.ceil(TESTIMONIALS.length * 2))].map((_, index) => (
          <div key={`left-${index}`} className="rounded-2xl bg-white opacity-30 min-h-[322px] w-[400px]"></div>
        ))}
      </div>

      {/* Right blank cards */}
      <div className="absolute right-0 top-0 hidden md:flex flex-col gap-6 translate-x-[calc(100%+8px)]">
        {[...Array(Math.ceil(TESTIMONIALS.length * 2))].map((_, index) => (
          <div key={`right-${index}`} className="rounded-2xl bg-white opacity-30 min-h-[322px] w-[400px]"></div>
        ))}
      </div>
    </div>
  );
}

export function NumbersSection() {
  return (
    <div className="px-4 w-full my-4 md:my-6 max-w-(--breakpoint-2xl)">
      <div className="partners w-full bg-ash-900 min-h-[463px] rounded-3xl flex flex-col items-center px-6 py-16 md:px-16 md:py-24 gap-16 relative">
        <NumbersArrow className="hidden md:block absolute top-10 left-10" />
        <h3 className="font-clash font-medium text-4xl md:text-6xl leading-[1.1] tracking-snug text-center text-white relative">
          Rebolt in{" "}
          <span className="relative inline-block">
            numbers
            <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-full">
              <UnderlineSVG className="w-full" />
            </span>
          </span>
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 w-full">
          <div className="flex flex-col gap-2 bg-ash-800 rounded-lg p-6 ">
            <h4 className="font-clash font-medium text-4xl md:text-6xl leading-[1.1] tracking-snug text-rebolt-500">
              500+
            </h4>
            <p className="text-lg text-ash-200 font-normal">service pros helped</p>
          </div>
          <div className="flex flex-col gap-2 bg-ash-800 rounded-lg p-6 ">
            <h4 className="font-clash font-medium text-4xl md:text-6xl leading-[1.1] tracking-snug text-rebolt-500">
              4,000+
            </h4>
            <p className="text-lg text-ash-200 font-normal">leads generated</p>
          </div>
          <div className="flex flex-col gap-2 bg-ash-800 rounded-lg p-6 ">
            <h4 className="font-clash font-medium text-4xl md:text-6xl leading-[1.1] tracking-snug text-rebolt-500">
              7,000,000+
            </h4>
            <p className="text-lg text-ash-200 font-normal">pageviews for customers monthly</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function FAQSection() {
  return (
    <div className="px-4 w-full my-16 md:my-24 max-w-[1260px] relative isolate">
      <div className="relative isolate">
        <Image src="/HeroGrid.png" alt="grid" fill className="absolute inset-0 w-full object-cover -z-10 " />
        <div className="w-full flex flex-col gap-16 relative">
          <div className="text-center items-center md:text-left flex flex-col md:flex-row gap-6 md:items-start justify-between">
            <h3 className="font-clash font-medium text-4xl md:text-6xl leading-[1.1] tracking-snug max-w-[432px]">
              Frequently asked{" "}
              <span className="relative inline-block">
                questions
                <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-full">
                  <UnderlineSVG className="w-full" />
                </span>
              </span>
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
            {FAQ_ITEMS.map((item, index) => (
              <div key={index} className="flex flex-col gap-3 bg-white rounded-3xl p-5">
                <h4 className="font-clash font-medium text-xl md:text-3xl leading-[1.1] tracking-snug">
                  {item.question}
                </h4>
                <p className="text-base md:text-lg font-normal text-ash-700">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function OffersSection() {
  return (
    <div className="px-4 lg:px-6 w-full max-w-(--breakpoint-2xl) relative isolate">
      <div className="relative isolate">
        <Image src="/HeroGrid.png" alt="grid" fill className="absolute inset-0 w-full object-cover -z-10 " />
        <div className="my-16 md:my-24 relative">
          <div className="flex flex-col items-center justify-center gap-12 md:gap-16">
            <div className="flex flex-col items-center justify-center gap-8">
              <div className="relative flex flex-col items-center text-center justify-center gap-6 max-w-[952px] text-ash-900">
                {/* <LeftArrow className="absolute bottom-0 -left-28" />
                <RightArrow className="absolute top-0 -right-28" /> */}
                <p className="text-lg md:text-xl text-rebolt-500 font-semibold uppercase line-height-[1.5] tracking-[0.05em]">
                  Simpler, Faster, Better
                </p>
                <h3 className="font-clash font-medium text-4xl md:text-6xl leading-[1.1] tracking-snug ">
                  We handle it all for you
                </h3>
                <p className="text-lg md:text-2xl">
                  You don't need a team of in-house experts to grow your business. <br className="hidden md:block" />{" "}
                  Focus on your trade and we'll handle the rest.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProductCardsSection() {
  return (
    <div className="flex flex-col gap-4 md:gap-6 mt-6 px-2 md:px-6 w-full max-w-(--breakpoint-2xl)">
      {PRODUCTS.map((product, index) => (
        <section
          key={product.slug}
          id={product.slug}
          className={cn(
            "w-full min-h-[500px] rounded-3xl grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 justify-between items-center py-16 md:p-16 px-4 scroll-mt-24",
            index % 2 === 0 ? "bg-white" : "relative"
          )}
        >
          {index % 2 === 1 && (
            <Image
              src="/ProductDemoGridBG.png"
              alt="Product"
              fill
              className="object-cover w-full h-full absolute inset-0 hidden lg:block"
            />
          )}

          <div
            className={cn(
              "flex flex-col gap-6 max-w-xl z-10",
              index % 2 === 1 ? "order-first lg:order-last relative" : ""
            )}
          >
            <div className="flex flex-col gap-6 text-ash-900">
              <div className="flex flex-col gap-4">
                <h3 className="line-height-[1.1] tracking-snug font-medium text-4xl md:text-6xl font-clash">
                  {product.longName}
                </h3>
                <p className="text-base md:text-lg leading-normal tracking-snug">{product.description}</p>
              </div>
              <div className="flex flex-col gap-2">
                {product.details.map((detail, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CircleCheckBig className="w-5 h-5 text-rebolt-500" />
                    <p className="text-base leading-normal tracking-snug font-medium">{detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Image
            src={product.demoImage}
            alt={product.alt}
            width={1000}
            height={1000}
            className={cn(
              "object-cover rounded-lg w-full h-full z-10",
              index % 2 === 1 ? "order-last lg:order-first" : "",
              product.className || ""
            )}
          />
        </section>
      ))}
    </div>
  );
}

export function ComparisonTable() {
  const FEATURES = [
    { name: "SEO-optimized", checked: ["rebolt", "agencies"] },
    { name: "Unlimited website changes", checked: ["rebolt"] },
    { name: "Affordable", checked: ["rebolt", "freelancers"] },
    { name: "Ready in hours", checked: ["rebolt"] },
    { name: "Pay up front", checked: ["agencies", "freelancers"] },
    { name: "Long-term contracts", checked: ["agencies"] },
  ];

  return (
    <div className="w-full max-w-[1114px] mx-auto">
      <div className="rounded-[32px] overflow-hidden border-8 border-white">
        <div className="overflow-x-auto">
          <div className="min-w-[768px]">
            <Table>
              <TableHeader>
                <TableRow className="border-b-0 bg-ash-900 text-white ">
                  <TableHead
                    className={"font-clash font-medium text-2xl md:text-2xl leading-[1.1]   py-5 w-1/4 text-center"}
                  ></TableHead>
                  <TableHead
                    className={"font-clash font-medium text-2xl md:text-2xl leading-[1.1] py-5 w-1/4 text-center"}
                  >
                    Rebolt
                  </TableHead>
                  <TableHead
                    className={"font-clash font-medium text-2xl md:text-2xl leading-[1.1] py-5 w-1/4 text-center"}
                  >
                    Agencies
                  </TableHead>
                  <TableHead
                    className={"font-clash font-medium text-2xl md:text-2xl leading-[1.1]  py-5 w-1/4 text-center"}
                  >
                    Freelancers
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {FEATURES.map((feature, index) => (
                  <TableRow key={index} className={"bg-white pt-1"}>
                    <TableCell className={"text-lg font-normal p-6"}>{feature.name}</TableCell>
                    <TableCell className="bg-ash-100 text-center py-6">
                      {feature.checked.includes("rebolt") ? (
                        <CircleCheckBig className="text-rebolt-500 w-6 h-6 mx-auto" strokeWidth={2.5} />
                      ) : (
                        <X className="text-ash-400 w-6 h-6 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center py-6">
                      {feature.checked.includes("agencies") ? (
                        <CircleCheckBig className="text-rebolt-500 w-6 h-6 mx-auto" strokeWidth={2.5} />
                      ) : (
                        <X className="text-ash-400 w-6 h-6 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center py-6">
                      {feature.checked.includes("freelancers") ? (
                        <CircleCheckBig className="text-rebolt-500 w-6 h-6 mx-auto" strokeWidth={2.5} />
                      ) : (
                        <X className="text-ash-400 w-6 h-6 mx-auto" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
